import { Icon } from '@iconify/react';

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BulkImportResult = ({ result, onDone }) => {
    if (!result) return null;

    const { successCount = 0, errorCount = 0, created = [], errors = {} } = result;
    const totalGroups = successCount + errorCount;
    const allSuccess = errorCount === 0;
    const { rowErrors = [], groupErrors = [], persistErrors = [] } = errors;
    const allErrors = [...rowErrors, ...groupErrors, ...persistErrors];

    return (
        <div>
            {/* Result Card */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-6 tw-text-center">
                <div className={`tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto ${
                    allSuccess ? 'tw-bg-emerald-50' : 'tw-bg-amber-50'
                }`}>
                    <Icon
                        icon={allSuccess ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'}
                        className={`tw-text-3xl ${allSuccess ? 'tw-text-emerald-500' : 'tw-text-amber-500'}`}
                    />
                </div>

                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-3">
                    {allSuccess ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                </h3>
                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                    {allSuccess
                        ? `All ${successCount} journal entries have been created successfully.`
                        : `${successCount} of ${totalGroups} entries created. ${errorCount} had errors.`
                    }
                </p>

                {/* Stats */}
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-mt-5">
                    <div className="tw-text-center">
                        <p className="tw-text-lg tw-font-semibold tw-text-gray-800">{totalGroups}</p>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Total</p>
                    </div>
                    <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                    <div className="tw-text-center">
                        <p className="tw-text-lg tw-font-semibold tw-text-emerald-600">{successCount}</p>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Created</p>
                    </div>
                    <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                    <div className="tw-text-center">
                        <p className="tw-text-lg tw-font-semibold tw-text-red-600">{errorCount}</p>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Failed</p>
                    </div>
                </div>
            </div>

            {/* Created Entries */}
            {created.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                    <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-500 tw-text-sm" />
                        <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Created Entries ({created.length})</h4>
                    </div>
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Voucher ID</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Journal ID</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Date</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Type</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Debits</th>
                                    <th className="tw-text-center tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Credits</th>
                                    <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {created.map((entry, idx) => (
                                    <tr key={idx} className="tw-border-b tw-border-gray-50">
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700 tw-font-medium">{entry.voucher_id}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-500">#{entry.journal_id}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700">{entry.date}</td>
                                        <td className="tw-px-3 tw-py-2">
                                            <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-blue-50 tw-text-blue-600">
                                                {entry.entry_type}
                                            </span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-600">{entry.debit_count}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-600">{entry.credit_count}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-font-medium">₹{formatCurrency(entry.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Error Details */}
            {allErrors.length > 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                    <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:alert-outline" className="tw-text-red-500 tw-text-sm" />
                        <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Failed Entries</h4>
                    </div>
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Row</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Voucher ID</th>
                                    <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Errors</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allErrors.map((err, idx) => (
                                    <tr key={idx} className="tw-border-b tw-border-gray-50 tw-bg-red-50/50">
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-500">{err.rowNum || err.rows?.join(', ') || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700">{err.voucher_id || '-'}</td>
                                        <td className="tw-px-3 tw-py-2">
                                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                                                {(err.errors || []).map((e, i) => (
                                                    <span key={i} className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-medium tw-bg-red-100 tw-text-red-600">
                                                        {e}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Action Button */}
            <div className="tw-flex tw-items-center tw-justify-end tw-mt-4">
                <button
                    onClick={onDone}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-transition-colors"
                >
                    <Icon icon="mdi:book-open-page-variant" className="tw-text-sm" />
                    Go to Journals
                </button>
            </div>
        </div>
    );
};

export default BulkImportResult;
